import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, User, ArrowRight, Menu, X } from 'lucide-react';
import SEO from '../components/SEO';
import './InfoPage.css';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  author_name: string;
  category: string;
  published_at: string;
}

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog?limit=10`);
      const data = await response.json();

      if (data.success) {
        setBlogPosts(data.data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="info-page">
      <SEO
        title="Blog - Construction Technology Insights | HomeQuest Tech"
        description="Stay updated on construction technology trends, AI automation, project management tips, and industry insights. Learn how to build better with AI-powered construction software."
        keywords="construction blog, construction technology, AI construction, project management tips, building automation, construction software insights, contractor tips"
        canonical="/blog"
      />
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <BookOpen className="badge-icon" />
              <span>BLOG & UPDATES</span>
            </div>
            <h1 className="hero-title">
              Latest <span className="gradient-text">Insights</span>
            </h1>
            <p className="hero-subtitle">
              Stay updated on construction technology trends, product updates, and industry insights from the HomeQuest team.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>
              <p>Loading blog posts...</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>
              <p>No blog posts available yet. Check back soon!</p>
            </div>
          ) : blogPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              onClick={() => navigate(`/blog/${post.slug}`)}
              style={{
                padding: '2rem',
                background: 'rgba(31, 41, 55, 0.6)',
                borderRadius: '16px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                marginBottom: '2rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(59, 130, 246, 0.2)';
                e.currentTarget.style.borderColor = '#3B82F6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
              }}
            >
              <div style={{ display: 'inline-block', padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '6px', fontSize: '0.875rem', color: '#60A5FA', marginBottom: '1rem' }}>
                {post.category}
              </div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#F9FAFB', marginBottom: '1rem' }}>
                {post.title}
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#D1D5DB', lineHeight: '1.7', marginBottom: '1.5rem' }}>
                {post.excerpt}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.875rem', color: '#9CA3AF' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar style={{ width: '16px', height: '16px' }} />
                  {formatDate(post.published_at)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <User style={{ width: '16px', height: '16px' }} />
                  {post.author_name}
                </div>
              </div>
            </motion.div>
          ))}

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <p style={{ fontSize: '1.125rem', color: '#9CA3AF', marginBottom: '2rem' }}>
              More articles coming soon! Subscribe to stay updated on construction technology insights.
            </p>
            <button onClick={() => navigate('/contact')} className="cta-button">
              Get in Touch <ArrowRight style={{ width: '20px', height: '20px' }} />
            </button>
          </div>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/about')}>About</a>
              <a onClick={() => navigate('/contact')}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
